import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ackDocument,
  fetchDocumentDetail,
  fetchDocumentDownloadUrl,
} from "./api";

export function DocumentViewPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("es") ? "es-ES" : "en-US";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["doc-detail", id],
    queryFn: () => fetchDocumentDetail(id),
    enabled: !!id,
  });

  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ackMutation = useMutation({
    mutationFn: () => ackDocument(id),
    onSuccess: (data) => {
      qc.setQueryData(["doc-detail", id], data);
      qc.invalidateQueries({ queryKey: ["docs-my"] });
    },
    onError: () => setError(t("docs.view.error_sign")),
  });

  const downloadMutation = useMutation({
    mutationFn: () => fetchDocumentDownloadUrl(id),
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: () => setError(t("docs.view.error_download")),
  });

  if (query.isLoading) {
    return (
      <div className="my-documents-root">
        <p>{t("docs.view.loading")}</p>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="my-documents-root">
        <p className="flash flash-error">{t("docs.view.error_load")}</p>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate("/documents")}
        >
          {t("docs.action.back_to_list")}
        </button>
      </div>
    );
  }

  const doc = query.data;
  const folderLabel = doc.folder?.name || t("docs.no_folder");
  const pendingAck = doc.requires_ack && !doc.acknowledged_at;

  return (
    <div className="my-documents-root">
      <div className="page-header doc-view-header">
        <div>
          <h1 className="page-title">{doc.title}</h1>
          <p className="page-subtitle">{t("docs.view.subtitle")}</p>
          <div className="doc-view-meta">
            <span className="doc-view-folder">{folderLabel}</span>
            <span className="doc-dot">•</span>
            <span className="doc-view-date">
              {formatDateTime(doc.created_at, locale)}
            </span>
          </div>
        </div>
        <div className="doc-view-status">
          {pendingAck && (
            <span className="status-pill status-warning">
              {t("docs.pill.required")}
            </span>
          )}
          {!pendingAck && doc.requires_ack && (
            <span className="status-pill status-approved">
              {t("docs.pill.signed")}
            </span>
          )}
          {!doc.requires_ack && (
            <span className="status-pill status-neutral">
              {t("docs.pill.no_signature")}
            </span>
          )}
        </div>
      </div>

      {error && <p className="flash flash-error">{error}</p>}

      {pendingAck && !doc.can_view ? (
        <div className="card doc-gate-card">
          <div className="doc-gate-header">
            <span className="status-pill status-warning">
              {t("docs.pill.required")}
            </span>
            <div className="summary-card-meta">{t("docs.view.gate_intro")}</div>
          </div>

          <div className="doc-gate-body">
            <div className="doc-gate-meta">
              <div className="summary-card-meta">{t("docs.view.file_name")}:</div>
              <div className="doc-gate-value">{doc.original_filename}</div>
            </div>
            {doc.size_bytes != null && (
              <div className="doc-gate-meta">
                <div className="summary-card-meta">{t("docs.view.file_size")}:</div>
                <div className="doc-gate-value">{doc.size_bytes} bytes</div>
              </div>
            )}
          </div>

          <form
            className="doc-gate-actions"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              if (!confirmed) return;
              ackMutation.mutate();
            }}
          >
            <label className="inline-check">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                required
              />
              <span>{t("docs.view.confirm")}</span>
            </label>
            <div className="doc-gate-buttons">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!confirmed || ackMutation.isPending}
              >
                {ackMutation.isPending
                  ? t("docs.view.signing")
                  : t("docs.view.sign_open")}
              </button>
              <Link to="/documents" className="btn btn-secondary">
                {t("docs.action.back_to_list")}
              </Link>
            </div>
          </form>
        </div>
      ) : (
        <>
          <div className="card doc-viewer-card">
            {doc.is_pdf && doc.view_url && (
              <iframe
                className="doc-viewer-frame"
                src={doc.view_url}
                title={doc.title}
              />
            )}
            {doc.is_image && doc.view_url && (
              <img
                className="doc-viewer-image"
                src={doc.view_url}
                alt={doc.title}
              />
            )}
            {!doc.is_pdf && !doc.is_image && (
              <p>{t("docs.view.preview_unavailable")}</p>
            )}
          </div>

          <div className="doc-view-toolbar">
            <div className="doc-view-toolbar-left">
              {doc.requires_ack && doc.acknowledged_at ? (
                <>
                  <span className="status-pill status-approved">
                    {t("docs.pill.signed")}
                  </span>
                  <span className="text-muted">
                    {t("docs.view.signed_on", {
                      date: formatDateTime(doc.acknowledged_at, locale),
                    })}
                  </span>
                </>
              ) : doc.requires_ack ? (
                <span className="status-pill status-warning">
                  {t("docs.pill.required")}
                </span>
              ) : (
                <span className="status-pill status-neutral">
                  {t("docs.pill.no_signature")}
                </span>
              )}
            </div>
            <div className="doc-view-toolbar-actions">
              <Link to="/documents" className="btn btn-secondary btn-sm">
                {t("docs.action.back")}
              </Link>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setError(null);
                  downloadMutation.mutate();
                }}
                disabled={downloadMutation.isPending}
              >
                {t("docs.action.download")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function formatDateTime(iso: string | null, locale: string): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
